import { useSelector } from 'react-redux';
import { map } from 'lodash';
import PropTypes from 'prop-types';
import pluralize from 'pluralize';
import { getDiscount, price, processPassengerType } from 'utils/helpers';
import {
  FORMAT_FULL_DATE_MINI,
  FORMAT_WEEKDAY_DATE_YEAR,
  formatDate,
} from 'utils/helpers/datetime';
import { useCurrentBookingState } from 'utils/hooks/use_current_booking_state';
import PromoCode from 'components/promo_code';
import {
  getAmountToPay,
  getCurrentBooking,
  getSelectedPaymentTypes,
  getTransactionFee,
} from 'redux/selectors/current_booking';
import CartBox from '../cart_box';
import { check0DepositAgent } from '../helpers';
import Surcharge from './surcharge';

export const CartSummary = ({
  readOnly = false,
  paid = false,
  title = 'Cart Summary',
}) => {
  const booking = useSelector(getCurrentBooking);
  const currentUser = useSelector((state) => state.user);
  const {
    state: bookingState,
    promo_code: existingPromoCode,
    reservations_attributes: reservations,
    passengers_attributes: passengers,
    paid_in_cents: paidInCents,
    deposit_relevant: depositRelevant,
    gross_in_cents: grossInCents,
    balance_in_cents: balanceInCents,
  } = booking;
  const passengerTypes = useSelector((state) => state.types.passengerTypes);
  const selectedPaymentType = useSelector(getSelectedPaymentTypes);
  const transactionFee = useSelector(getTransactionFee);
  const amountToPay = useSelector(getAmountToPay);
  const is0DepositAgent = check0DepositAgent(currentUser);
  const balanceDueOn = booking.balance_due_on?._value;
  const { isFullyPaid } = useCurrentBookingState();
  const discount = getDiscount(booking);
  const processedReservations = [];

  if (reservations) {
    reservations.forEach((reservation) => {
      const {
        id,
        travel_offset_days,
        first_travel_date,
        last_travel_date,
        selection_name,
        passenger_splits,
        sub_reservations_attributes,
        resource_class_name,
        selection_display_name,
        adjustments_attributes,
      } = reservation;
      let date = formatDate(first_travel_date._value, FORMAT_WEEKDAY_DATE_YEAR);

      if (travel_offset_days) {
        date = `Valid until ${formatDate(
          last_travel_date._value,
          FORMAT_FULL_DATE_MINI
        )}`;
      }

      const adjustmentGroups = {};
      (adjustments_attributes || [])
        .filter((adj) => adj.display_to_customer)
        .forEach((adj) => {
          const key = adj.adjustment_category_id;
          if (!adjustmentGroups[key]) {
            adjustmentGroups[key] = {
              category_id: key,
              display_name: adj.display_name,
              total: 0,
            };
          }
          adjustmentGroups[key].total += adj.gross_in_cents;
        });

      const processedReservation = {
        id,
        name: selection_display_name || selection_name,
        date,
        passengerDetail: processPassengerType(
          passenger_splits,
          passengers,
          passengerTypes
        ),
        subReservations: sub_reservations_attributes || [],
        resource_class_name,
        customerFacingAdjustments: Object.values(adjustmentGroups),
      };

      processedReservations.push(processedReservation);
    });
  }

  let priceLabel = 'Total to pay';
  if (selectedPaymentType?.payment_method === 'on_account_with_reference') {
    priceLabel = 'Total to Collect';
  }
  if (is0DepositAgent) {
    priceLabel = 'Total Agent to Collect';
  }

  return (
    <CartBox className="summary-card divider-y-3" title={title}>
      {processedReservations &&
        processedReservations.map((reservation) => (
          <div key={reservation.id} className="summary-card__item space-y-2">
            <div className="summary-card__name">{reservation.name}</div>
            <div className="summary-card__date">
              <i className="far fa-calendar-alt me-2" />
              {reservation.date}
            </div>
            <div className="summary-card__flex-list">
              {map(reservation.passengerDetail, (value, key) => (
                <div key={key} className="summary-card__flex-item text-small">
                  <div>{pluralize(key, value.count, true)}</div>
                  {value.price > 0 && (
                    <div>
                      {value.count} × {price(value.price)}
                    </div>
                  )}
                </div>
              ))}
            </div>
            {reservation.resource_class_name !== 'Package' &&
            reservation.subReservations.length ? (
              <>
                <div className="summary-card__name">Add-ons</div>
                {reservation.subReservations.map((subReservation) => (
                  <div
                    className="summary-card__flex-list"
                    key={subReservation.id}
                  >
                    <div className="summary-card__flex-item text-small">
                      <div>
                        {subReservation.selection_display_name ||
                          subReservation.selection_name}
                      </div>
                      <div>
                        {price(subReservation.pre_adjusted_gross_in_cents)}
                      </div>
                    </div>
                  </div>
                ))}
              </>
            ) : (
              ''
            )}
            {reservation.customerFacingAdjustments.map((group) => (
              <div className="summary-card__flex-list" key={group.category_id}>
                <div className="summary-card__flex-item text-small">
                  <div>{group.display_name || 'Surcharge'}</div>
                  <div>{price(group.total)}</div>
                </div>
              </div>
            ))}
          </div>
        ))}
      {(!existingPromoCode && readOnly) || bookingState === 'active' ? (
        ''
      ) : (
        <div className="summary-card__item space-y-2">
          <div className="summary-card__name">Promo code</div>
          <PromoCode buttonClassName="btn btn-secondary" />
        </div>
      )}

      <Surcharge />

      {paid ? (
        <>
          <div className="summary-card__item space-y-2 mt-3 pt-4">
            {Boolean(discount) && (
              <div className="summary-card__flex-item">
                <div>Discount</div>
                <div>-{price(discount)}</div>
              </div>
            )}
            <div className="summary-card__flex-item summary-card__total">
              <div>{isFullyPaid ? 'Total Paid' : 'Total'}</div>
              <div>{price(isFullyPaid ? paidInCents : grossInCents)}</div>
            </div>
          </div>
          {!isFullyPaid && (
            <div className="summary-card__item space-y-2 mt-3 pt-4">
              {depositRelevant && (
                <div className="summary-card__flex-item summary-card__deposit">
                  <div>Deposit paid</div>
                  <div>{price(paidInCents)}</div>
                </div>
              )}
              <div className="summary-card__flex-item summary-card__balance">
                <div>
                  Remaining balance
                  <br />
                  <span className="summary-card__due-date">
                    Due by {formatDate(balanceDueOn, FORMAT_FULL_DATE_MINI)}
                  </span>
                </div>
                <div>{price(balanceInCents)}</div>
              </div>
            </div>
          )}
        </>
      ) : (
        <div className="summary-card__item space-y-2 mt-3 pt-4">
          <div className="summary-card__flex-item summary-card__total">
            <div>{priceLabel}</div>
            <div>{price((amountToPay || 0) * (1 + transactionFee / 100))}</div>
          </div>
        </div>
      )}
    </CartBox>
  );
};

CartSummary.propTypes = {
  readOnly: PropTypes.bool,
  paid: PropTypes.bool,
  title: PropTypes.string,
};

export default CartSummary;
